import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Fuse from 'fuse.js';
import { graphRequest } from '../../msalConfig';
import { getItems, FAMILY_LISTS } from '../../api/figApi';
import { listPhotos } from '../api/photoApi';
import MediaCover from '../components/MediaCover';

const FUSE_OPTIONS = {
  keys: [
    { name: 'Title',           weight: 1.0 },
    { name: 'Artist',          weight: 0.8 },
    { name: 'BookAuthor',      weight: 0.8 },
    { name: 'Composer',        weight: 0.7 },
    { name: 'Tags',            weight: 0.7 },
    { name: 'Album',           weight: 0.6 },
    { name: 'SeriesName',      weight: 0.6 },
    { name: 'AlbumCollection', weight: 0.6 },
    { name: 'Work',            weight: 0.5 },
    { name: 'Narrator',        weight: 0.5 },
    { name: 'People',          weight: 0.4 },
    { name: 'Location',        weight: 0.4 },
    { name: 'Notes',           weight: 0.2 },
    { name: 'Witness',         weight: 0.15 },
    { name: 'Doctrines',       weight: 0.5 },
  ],
  threshold: 0.3,
  ignoreLocation: true,
  includeScore: false,
  minMatchCharLength: 2,
};

const TYPE_ORDER = ['Audiobook', 'Music', 'Movie', 'Video', 'Photo'];

const PLURAL = {
  Audiobook: 'Audiobooks',
  Music:     'Music',
  Movie:     'Movies',
  Video:     'Videos',
  Photo:     'Photos',
};

const SLUG = {
  Audiobook: 'audiobooks',
  Music:     'music',
  Movie:     'movies',
  Video:     'videos',
  Photo:     'photos',
};

const ASPECT = {
  Audiobook: '1:1',
  Music:     '1:1',
  Movie:     '2:3',
  Video:     '16:9',
  Photo:     '1:1',
};

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} ${d.getUTCFullYear()}`;
}

function secondaryLine(item) {
  switch (item.MediaType) {
    case 'Audiobook': return item.BookAuthor || '';
    case 'Music':     return item.Composer || item.Artist || '';
    case 'Movie':     return item.ReleaseYear ? String(item.ReleaseYear) : '';
    case 'Video':
    case 'Photo':     return item.AlbumCollection || formatEventDate(item.DateOfEvent) || '';
    default:          return '';
  }
}

export default function Search() {
  const { instance, accounts } = useMsal();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const [mediaItems, photos] = await Promise.all([
          getItems(token, FAMILY_LISTS.MEDIA_ITEMS),
          listPhotos(token).catch(() => []),
        ]);
        if (!cancelled) setItems([...mediaItems, ...photos]);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  const fuse = useMemo(() => {
    if (!items) return null;
    return new Fuse(items, FUSE_OPTIONS);
  }, [items]);

  const results = useMemo(() => {
    if (!fuse || q.length < 2) return [];
    return fuse.search(q).map(r => r.item);
  }, [fuse, q]);

  const grouped = useMemo(() => {
    const out = {};
    for (const type of TYPE_ORDER) {
      out[type] = results.filter(item => item.MediaType === type);
    }
    return out;
  }, [results]);

  const hasResults = TYPE_ORDER.some(t => grouped[t]?.length > 0);

  if (items === null && !error) {
    return <div className="FV-shelf"><p className="FV-empty">Loading…</p></div>;
  }

  if (error) {
    return <div className="FV-shelf"><p className="fv-browse-error">{error}</p></div>;
  }

  return (
    <div className="FV-shelf">
      <h1 className="FV-shelf-title">Search</h1>

      {q.length < 2 && (
        <p className="FV-search-empty">
          Type in the search bar to find anything in your library.
        </p>
      )}

      {q.length >= 2 && !hasResults && (
        <p className="FV-search-no-results">
          No results for "{q}". Try a different spelling or fewer words.
        </p>
      )}

      {q.length >= 2 && hasResults && TYPE_ORDER.map(type => {
        const typeResults = grouped[type];
        if (!typeResults.length) return null;
        const slug = SLUG[type];
        return (
          <section key={type} className="FV-search-group">
            <h2 className="FV-search-group-title">
              {PLURAL[type]} ({typeResults.length})
            </h2>
            <div className="fv-browse-grid">
              {typeResults.slice(0, 5).map(item => {
                const secondary = secondaryLine(item);
                return (
                  <Link
                    key={item.id}
                    to={`/${slug}/${item.id}`}
                    className="fv-browse-card"
                  >
                    <MediaCover item={item} aspectRatio={ASPECT[type]} size="md" />
                    <span className="fv-browse-card-title">{item.Title}</span>
                    {secondary && (
                      <span className="fv-browse-card-secondary">{secondary}</span>
                    )}
                  </Link>
                );
              })}
            </div>
            {typeResults.length > 5 && (
              <Link
                to={`/${slug}?q=${encodeURIComponent(q)}`}
                className="FV-search-see-all"
              >
                See all {typeResults.length} results in {PLURAL[type]} →
              </Link>
            )}
          </section>
        );
      })}
    </div>
  );
}
